/**
 * AbilityScenariosTab — bot scenario spawning and stress harness.
 *
 * Modernized segmented bot count picker and themed scenario dispatch buttons.
 */
import React from 'react';
import {
  GROUNDS_SCENARIO_MAX_COUNT,
  LOBBY_TOOLS,
  type LobbyToolId,
} from '../../../../shared/constants/LobbyTools';
import { Button, Card, Badge } from '../../primitives';
import {
  COLORS,
  FONTS,
  FONT_SIZE,
  FONT_WEIGHT,
  PLAYER_UI,
  RADII,
  SPACE,
} from '../../theme';
import { SCENARIO_SPAWN_TOOLS, sendScenarioTool } from './types';

interface AbilityScenariosTabProps {
  scenarioCount: number;
  setScenarioCount: (count: number) => void;
  setLastAction: (action: string) => void;
}

const PRESET_COUNTS = [1, 2, 3, 5, 10];

export const AbilityScenariosTab: React.FC<AbilityScenariosTabProps> = ({
  scenarioCount,
  setScenarioCount,
  setLastAction,
}) => {
  const spawnScenario = (tool: LobbyToolId) => {
    const definition = SCENARIO_SPAWN_TOOLS.find((candidate) => candidate.id === tool);
    const sent = sendScenarioTool(tool, scenarioCount);
    setLastAction(
      sent
        ? `Scenario requested · ${scenarioCount}× ${definition?.label ?? tool}`
        : 'No live room is connected.',
    );
  };

  const clearScenarioBots = () => {
    const sent = sendScenarioTool(LOBBY_TOOLS.DESPAWN_SCENARIO_BOTS);
    setLastAction(sent ? 'Scenario bots clear requested.' : 'No live room is connected.');
  };

  return (
    <div style={styles.container}>
      <Card
        title="Stress Scenarios & Bots"
        subtitle="Optional bot encounters; the fixed dummy lane stays canonical"
        icon="⚙"
      >
        {/* Count Segmented Row */}
        <div style={styles.scenarioHeader}>
          <span style={styles.fieldLabel}>Bot Count</span>
          <div style={styles.countPills}>
            {PRESET_COUNTS.map((count) => {
              const isActive = scenarioCount === count;
              return (
                <button
                  key={count}
                  type="button"
                  style={{
                    ...styles.countPill,
                    ...(isActive ? styles.countPillActive : {}),
                  }}
                  onClick={() => setScenarioCount(count)}
                >
                  {count}
                </button>
              );
            })}
          </div>
          {/* Preserved native select for test harness compatibility */}
          <select
            id="grounds-scenario-count"
            value={scenarioCount}
            onChange={(event) => setScenarioCount(Number(event.target.value))}
            style={styles.hiddenSelect}
            data-testid="grounds-scenario-count"
            aria-hidden="true"
          >
            {Array.from({ length: GROUNDS_SCENARIO_MAX_COUNT }, (_, index) => index + 1).map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
          <Badge tone={scenarioCount === 1 ? 'info' : 'warning'} size="sm">
            {scenarioCount === 1 ? '1v1 DUEL' : `1v${scenarioCount} SWARM`}
          </Badge>
        </div>

        {/* Action Button Grid */}
        <div style={styles.scenarioGrid}>
          {SCENARIO_SPAWN_TOOLS.map((definition) => (
            <Button
              key={definition.id}
              size="sm"
              variant="secondary"
              title={definition.description}
              onClick={() => spawnScenario(definition.id)}
              data-testid={`grounds-${definition.id}`}
            >
              {definition.icon} {definition.label}
            </Button>
          ))}
          <Button
            size="sm"
            variant="danger"
            onClick={clearScenarioBots}
            data-testid="grounds-despawn-scenario-bots"
          >
            🧹 Clear Bots
          </Button>
        </div>
      </Card>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.sm,
  },
  scenarioHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.sm,
    marginBottom: SPACE.sm,
    padding: `${SPACE.xxs}px ${SPACE.xs}px`,
    background: 'rgba(0, 0, 0, 0.25)',
    borderRadius: RADII.sm,
    border: `1px solid ${COLORS.borderSubtle}`,
  },
  fieldLabel: {
    color: COLORS.textMuted,
    fontSize: FONT_SIZE.xs,
    fontWeight: FONT_WEIGHT.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  countPills: {
    display: 'flex',
    gap: 3,
  },
  countPill: {
    padding: '3px 8px',
    background: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    color: COLORS.textSecondary,
    fontSize: FONT_SIZE.xs,
    fontFamily: FONTS.mono,
    fontWeight: FONT_WEIGHT.bold,
    cursor: 'pointer',
    transition: 'all 0.12s ease',
  },
  countPillActive: {
    background: 'rgba(196, 164, 105, 0.25)',
    border: `1px solid ${PLAYER_UI.brassBright}`,
    boxShadow: '0 0 6px rgba(196, 164, 105, 0.4)',
    color: PLAYER_UI.brassBright,
  },
  hiddenSelect: {
    display: 'none',
  },
  scenarioGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: SPACE.xs,
  },
};
